import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Person, People, Public } from '@mui/icons-material';
import { COLOR, FOCUS_VISIBLE_SX, RADIUS } from '../ui/tokens';

export const TJP_MODES = [
    {
        value: 'single',
        label: '1 Player',
        hint: 'Balance the double pole as long as you can',
        icon: Person,
    },
    {
        value: 'multi',
        label: '2 Players',
        hint: 'Vs computer or local 2P on one keyboard',
        icon: People,
    },
    {
        value: 'online',
        label: 'Online 2P',
        hint: 'Play someone on another computer',
        icon: Public,
    },
];

/**
 * Two-Joint Pole mode picker (1P / 2P / Online 2P) for the page header. Locked while a
 * run is live so switching cannot drop a session mid-episode.
 */
export default function TwoJointPoleModePicker({ value, onChange, accentColor = COLOR.primary, disabled = false }) {
    return (
        <Box
            role="radiogroup"
            aria-label="Game mode"
            data-testid="tjp-mode-picker"
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr 1fr', sm: 'repeat(3, minmax(160px, 210px))' },
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
            }}
        >
            {TJP_MODES.map(({ value: mode, label, hint, icon: Icon }) => {
                const selected = value === mode;
                return (
                    <ButtonBase
                        key={mode}
                        role="radio"
                        aria-checked={selected}
                        disabled={disabled && !selected}
                        onClick={() => { if (!selected) onChange(mode); }}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.25,
                            p: 1.5,
                            textAlign: 'left',
                            borderRadius: RADIUS.md,
                            border: `1px solid ${selected ? accentColor : COLOR.borderStrong}`,
                            bgcolor: selected ? 'rgba(142,140,216,0.12)' : COLOR.surface,
                            color: COLOR.text,
                            '&.Mui-disabled': { opacity: 0.45 },
                            '&:hover': { borderColor: accentColor },
                            ...FOCUS_VISIBLE_SX,
                        }}
                    >
                        <Icon sx={{ color: selected ? accentColor : COLOR.textMuted, mt: 0.25 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{label}</Typography>
                            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.75rem', lineHeight: 1.35 }}>
                                {hint}
                            </Typography>
                        </Box>
                    </ButtonBase>
                );
            })}
        </Box>
    );
}
